import { useRouter } from 'next/router';
import '@vibTheme/globals.css';
import type { AppProps } from 'next/app';
import { ThemeProvider } from '@mui/material';
import muiTheme from '@vibTheme/mui-theme';
import { Layouts } from '@interfaces/layouts';
import Layout from '@components/Layout';
import LayoutAppbarOnly from '@components/Layout/AppbarOnly';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { SessionProvider, useSession, signIn } from 'next-auth/react';
import { ToastContextProvider } from '../contexts/ToastContext';
import { GroupsContextProvider } from '../contexts/GroupsContext';
import { poppins } from '@vibTheme/fonts';
import { PropsWithChildren, useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { saveRefToLocalStorage } from '@utils/captureRef';
import { trackReferralLinkOpen } from '@utils/trackReferralLinkOpen';
import { ENTRY_SEARCH } from '@utils/entrySearch';
import { useCapturaDeOrigem } from '@hooks/useCapturaDeOrigem';
import { CurrentProductContextProvider } from '@contexts/CurrentProductContext';
import { FeaturePermissionsContextProvider } from '@contexts/FeaturePermissionsContext';
import { VibLoadingOverlay } from '@vibTheme/components/VibLoadingOverlay';
import { PageTypes } from '@interfaces/pageType';
import { AppCacheProvider } from '@mui/material-nextjs/v13-pagesRouter';
import { User2faContextProvider } from '@contexts/User2faContext';
import { TourContextProvider } from '@contexts/TourContext';
import { isProdClient } from '@utils/isProdClient';
import {
  initGoogleAnalytics,
  setAnalyticsUserId,
  trackNavigation,
} from '@utils/googleAnalytics';
import { endMetaIdentity, pushMetaIdentity } from '@utils/metaIdentity';
import { ProfileContextProvider } from '@contexts/ProfileContext';
import { useExpiraVoltaParaHub } from '@components/Onboarding/hub';
import { Seo } from '@components/Seo';

const cache = createCache({
  key: 'css',
  prepend: true,
});

export default function MyApp(props: AppProps) {
  const router = useRouter();
  /*
    Aqui, e não no `useProductBackPath`: aquele hook só existe nas páginas que usam os
    layouts de produto, e a saída pelo breadcrumb "Comunidades" vai para `/products`,
    que não usa nenhum deles. O marcador sobrevivia à saída e rearmava numa visita
    seguinte que não partiu do hub.
  */
  useExpiraVoltaParaHub();

  useEffect(() => {
    if (isProdClient()) {
      TagManager.initialize({
        gtmId: String(process.env.NEXT_PUBLIC_GTM_ID),
      });
      /*
        Além do container, e não dentro dele: uma tag de configuração do GA4
        dentro do `GTM-KBF6J9QS` seria um segundo emissor para o mesmo
        measurement ID e dobraria a medição — ver `utils/googleAnalytics.ts`.
      */
      initGoogleAnalytics();
    }

    /*
      A query da ENTRADA, e não a de agora: este efeito é passivo e corre depois da pintura, e um
      clique num link já hidratado nessa janela executa o `pushState` antes — ali `location` já é o
      destino, e tanto a indicação quanto a origem de aquisição se perderiam. Ver
      `utils/entrySearch.ts`.
    */
    const urlParams = new URLSearchParams(ENTRY_SEARCH);
    const refValue = urlParams.get('referrerCode');
    if (refValue) {
      trackReferralLinkOpen(refValue).catch(console.error);
      saveRefToLocalStorage(refValue);
    }
  }, []);

  /*
    Vale para toda navegação, não só para as anteriores ao `gtag.js`: a contagem
    por evento de histórico está desligada no stream do GA4 — ela também contava
    reescrita de URL como página nova —, então sem este listener a medição
    pararia na página de entrada. Ver `utils/googleAnalytics.ts`.
  */
  useEffect(() => {
    router.events.on('routeChangeComplete', trackNavigation);
    return () => router.events.off('routeChangeComplete', trackNavigation);
  }, [router.events]);

  return (
    <AppCacheProvider {...props}>
      {/*
        Aqui, e não no `PageWrapper`: na carga inicial os três wrappers
        curto-circuitam no `VibLoadingOverlay` enquanto a sessão não resolve, e
        o `PageWrapper` nem chega a montar. O título ficava vazio até lá — a
        aba mostrava a URL crua, e o `page_view` de entrada saía sem
        `page_title`, porque `initGoogleAnalytics` lê `document.title` no efeito
        de montagem deste componente.
      */}
      <Seo />
      <SessionProvider>
        {/*
          A captura da origem de aquisição fica AQUI DENTRO, e não no efeito acima: ela depende de
          saber se há alguém logado, e `useSession` só existe abaixo do provedor. Ver
          `useCapturaDeOrigem` — a query da entrada é um instantâneo de módulo, então esperar a
          sessão resolver não perde parâmetro nenhum.
        */}
        <CapturaDeOrigem />
        {renderPage(props.Component, props.pageProps)}
      </SessionProvider>
    </AppCacheProvider>
  );
}

/** Só existe para pendurar o efeito abaixo do `SessionProvider` — ver `useCapturaDeOrigem`. */
const CapturaDeOrigem = () => {
  useCapturaDeOrigem();

  return null;
};

const PageWrapper = (props: PropsWithChildren) => (
  <CacheProvider value={cache}>
    <ThemeProvider theme={muiTheme}>
      <ToastContextProvider>
        <TourContextProvider>
          <AnalyticsIdentity />
          <style jsx global>
            {`
              :root {
                --font-poppins: ${poppins.style.fontFamily};
              }
            `}
          </style>
          {props.children}
        </TourContextProvider>
      </ToastContextProvider>
    </ThemeProvider>
  </CacheProvider>
);

/**
 * Diz ao GA4 quem está usando o app.
 *
 * Componente próprio, e não um efeito do `PrivatePageWrapper`: aquele componente retorna cedo
 * (`signIn()`, overlay) antes de qualquer hook poder rodar, e um `useEffect` ali seria chamado
 * condicionalmente.
 *
 * Montado no `PageWrapper`, que é comum às páginas privadas, públicas e híbridas — e não só nas
 * privadas. É o que permite LIMPAR a identidade: a sessão do NextAuth dura quatro horas, e quem
 * ficasse numa rota híbrida depois dela vencer seguiria sendo medido como a conta anterior.
 *
 * Só o id numérico. Nada de e-mail, documento ou nome: o que se quer é poder juntar comportamento
 * de tela com `platform_user`, não identificar pessoa dentro do GA4.
 */
const AnalyticsIdentity = () => {
  const session = useSession();
  const userId = session.data?.user?.id;
  const status = session.status;

  useEffect(() => {
    /*
      `undefined` enquanto a sessão carrega — que é diferente de `null`. A sessão dura quatro horas
      e pode vencer com a pessoa parada numa rota híbrida; sem limpar, toda navegação anônima
      seguinte continuaria atribuída à conta anterior pelo resto da vida do documento.
    */
    if (status === 'loading') return;

    setAnalyticsUserId(status === 'authenticated' ? userId : null);
    // O mesmo id para o pixel do Meta; sem sessão, o pixel para. Ver `utils/metaIdentity`.
    if (status === 'authenticated') pushMetaIdentity(userId);
    else endMetaIdentity();
  }, [userId, status]);

  return null;
};

const PrivatePageWrapper = (props: PropsWithChildren) => {
  const session = useSession();
  if (session.status === 'unauthenticated') signIn();
  if (session.status !== 'authenticated') return <VibLoadingOverlay />;

  return (
    <PageWrapper>
      <GroupsContextProvider>
        <ProfileContextProvider>
          <FeaturePermissionsContextProvider>
            <CurrentProductContextProvider>
              <User2faContextProvider>{props.children}</User2faContextProvider>
            </CurrentProductContextProvider>
          </FeaturePermissionsContextProvider>
        </ProfileContextProvider>
      </GroupsContextProvider>
    </PageWrapper>
  );
};

/**
 * `renderizaSemSessao` no HÍBRIDO tem o mesmo motivo que no público, e uma consequência a mais.
 *
 * No servidor o `SessionProvider` monta sem sessão, então o status é SEMPRE `loading` — e sem a
 * marca este wrapper devolve o overlay. O HTML que sai do servidor não tem uma palavra do conteúdo;
 * ele só aparece depois do JS, no navegador.
 *
 * Para quem abre a página isso não muda nada. O que quebra é tudo que não executa JS: a indexação
 * (o Google só enxerga quando roda o script, dias depois), e a PRÉVIA DE LINK do WhatsApp e do
 * Slack — que numa página feita para ser colada em conversa é o caso principal, não um detalhe.
 *
 * A contrapartida é um piscar para quem está logado: o servidor manda a versão sem menu, e o menu
 * entra quando a sessão resolve. Por isso a marca é POR PÁGINA e nasce fechada — numa tela onde a
 * versão deslogada é um estado transitório feio, o overlay é preferível.
 */
const HybridPageWrapper = (
  props: PropsWithChildren<{ renderizaSemSessao?: boolean }>,
) => {
  const session = useSession();

  switch (session.status) {
    case 'loading':
      if (!props.renderizaSemSessao) return <VibLoadingOverlay />;
    // eslint-disable-next-line no-fallthrough
    case 'unauthenticated':
      return (
        <PageWrapper>
          <LayoutAppbarOnly>{props.children}</LayoutAppbarOnly>
        </PageWrapper>
      );

    case 'authenticated':
    default:
      return (
        <PrivatePageWrapper>
          <Layout>{props.children}</Layout>
        </PrivatePageWrapper>
      );
  }
};

/**
 * `renderizaSemSessao` existe para a página sair no HTML do servidor.
 *
 * Sem ele, o corpo de TODA página pública no SSR é o overlay: o `SessionProvider` é montado sem
 * `session`, então ali o status é sempre `loading`. A tela de login chegava ao Google como um
 * documento vazio, e o `<h1>Login</h1>` dela só aparecia quando a fila de renderização do Google
 * executasse o JS — dias depois de cada rastreio.
 *
 * Fechado por padrão: página que não declara a marca continua esperando a sessão como antes. Quem
 * declara aceita a contrapartida de nunca esconder nada atrás da autenticação — é o caso do login e
 * do cadastro, que não têm dado de sessão para mostrar.
 */
const PublicPageWrapper = (
  props: PropsWithChildren<{ renderizaSemSessao?: boolean }>,
) => {
  const session = useSession();
  if (session.status !== 'unauthenticated' && !props.renderizaSemSessao)
    return <VibLoadingOverlay />;

  return <PageWrapper>{props.children}</PageWrapper>;
};

const renderPage = (Component: any, pageProps: any) => {
  switch (Component.pageType) {
    case PageTypes.PUBLIC:
      return (
        <PublicPageWrapper renderizaSemSessao={Component.renderizaSemSessao}>
          {renderPageWithLayout(Component, pageProps)}
        </PublicPageWrapper>
      );

    case PageTypes.HYBRID:
      return (
        <HybridPageWrapper renderizaSemSessao={Component.renderizaSemSessao}>
          <Component {...pageProps} />
        </HybridPageWrapper>
      );

    case PageTypes.PRIVATE:
    default:
      return (
        <PrivatePageWrapper>
          {renderPageWithLayout(Component, pageProps)}
        </PrivatePageWrapper>
      );
  }
};

const renderPageWithLayout = (Component: any, pageProps: any) => {
  switch (Component.layout) {
    case Layouts.APPBARONLY:
      return (
        <LayoutAppbarOnly>
          <Component {...pageProps} />
        </LayoutAppbarOnly>
      );

    case Layouts.NONE:
      return <Component {...pageProps} />;

    case Layouts.DEFAULT:
    default:
      return (
        <Layout>
          <Component {...pageProps} />
        </Layout>
      );
  }
};
